/**
 * Guitar shapes for a chord name.
 *
 * Two sources, in order of preference:
 *
 * 1. **Open shapes**, because a real player reaches for those first — they ring,
 *    they are easy, and they are what a tab means when it writes G.
 * 2. **Barre shapes derived by moving a form up the neck.** Every major barre
 *    chord is E-shape or A-shape with a finger across; the same is true of the
 *    minors and sevenths. Deriving them beats storing hundreds of rows, and it
 *    cannot drift out of agreement with itself.
 *
 * Frets are per string, low E first. -1 is a muted string, 0 is open.
 */

export interface Shape {
  frets: number[];       // six entries, low E to high E
  baseFret: number;      // the fret the diagram starts at, 1 for open shapes
  barre?: number;        // fret to draw the barre across, relative to baseFret
}

const PITCH: Record<string, number> = {
  C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4, F: 5, "F#": 6, Gb: 6,
  G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11,
};

/** Open shapes worth preferring over any barre. */
const OPEN: Record<string, number[]> = {
  C: [-1, 3, 2, 0, 1, 0],
  A: [-1, 0, 2, 2, 2, 0],
  G: [3, 2, 0, 0, 0, 3],
  E: [0, 2, 2, 1, 0, 0],
  D: [-1, -1, 0, 2, 3, 2],
  Am: [-1, 0, 2, 2, 1, 0],
  Em: [0, 2, 2, 0, 0, 0],
  Dm: [-1, -1, 0, 2, 3, 1],
  A7: [-1, 0, 2, 0, 2, 0],
  B7: [-1, 2, 1, 2, 0, 2],
  C7: [-1, 3, 2, 3, 1, 0],
  D7: [-1, -1, 0, 2, 1, 2],
  E7: [0, 2, 0, 1, 0, 0],
  G7: [3, 2, 0, 0, 0, 1],
  Am7: [-1, 0, 2, 0, 1, 0],
  Dm7: [-1, -1, 0, 2, 1, 1],
  Em7: [0, 2, 0, 0, 0, 0],
  Cmaj7: [-1, 3, 2, 0, 0, 0],
  Fmaj7: [-1, -1, 3, 2, 1, 0],
  Gmaj7: [3, 2, 0, 0, 0, 2],
  Amaj7: [-1, 0, 2, 1, 2, 0],
  Dmaj7: [-1, -1, 0, 2, 2, 2],
  Asus4: [-1, 0, 2, 2, 3, 0],
  Dsus4: [-1, -1, 0, 2, 3, 3],
  Esus4: [0, 2, 2, 2, 0, 0],
  Asus2: [-1, 0, 2, 2, 0, 0],
  Dsus2: [-1, -1, 0, 2, 3, 0],
};

/**
 * Movable forms, as offsets from the root fret.
 * E-shape roots sit on the 6th string, A-shape roots on the 5th.
 */
const E_FORM: Record<string, number[]> = {
  "":     [0, 2, 2, 1, 0, 0],
  m:      [0, 2, 2, 0, 0, 0],
  "7":    [0, 2, 0, 1, 0, 0],
  m7:     [0, 2, 0, 0, 0, 0],
  maj7:   [0, 2, 1, 1, 0, 0],
  sus4:   [0, 2, 2, 2, 0, 0],
  m6:     [0, 2, 0, 0, 2, 0],
  "6":    [0, 2, 2, 1, 2, 0],
};
const A_FORM: Record<string, number[]> = {
  "":     [-1, 0, 2, 2, 2, 0],
  m:      [-1, 0, 2, 2, 1, 0],
  "7":    [-1, 0, 2, 0, 2, 0],
  m7:     [-1, 0, 2, 0, 1, 0],
  maj7:   [-1, 0, 2, 1, 2, 0],
  sus4:   [-1, 0, 2, 2, 3, 0],
  m6:     [-1, 0, 2, 0, 1, 2],
  "6":    [-1, 0, 2, 2, 2, 2],
};

const E_STRING = 4;    // open low E, in semitones from C
const A_STRING = 9;

function split(label: string): { root: string; quality: string } | null {
  const match = /^([A-G][#b]?)(.*)$/.exec(label.split("/")[0].trim());
  if (!match || !(match[1] in PITCH)) return null;
  let quality = match[2].replace(/\s+/g, "");
  // Colours we cannot draw are dropped to the triad they sit on, which is what
  // a player would grab anyway rather than skip the bar.
  if (/^(add9|9|11|13|sus2|maj9)$/.test(quality)) quality = quality === "sus2" ? "sus2" : "";
  if (/^m(9|11|13|add9)$/.test(quality)) quality = "m";
  return { root: match[1], quality };
}

function movable(rootPitch: number, quality: string): Shape | null {
  const forms: [Record<string, number[]>, number][] = [[E_FORM, E_STRING], [A_FORM, A_STRING]];
  const options: Shape[] = [];
  for (const [form, openPitch] of forms) {
    const shape = form[quality];
    if (!shape) continue;
    let fret = (((rootPitch - openPitch) % 12) + 12) % 12;
    if (fret === 0) fret = 12;                    // open handled elsewhere
    if (fret > 11) continue;
    options.push({
      frets: shape.map((f) => (f < 0 ? -1 : f + fret)),
      baseFret: fret,
      barre: fret,
    });
  }
  if (!options.length) return null;
  // Lowest position on the neck is the easiest reach.
  return options.sort((a, b) => a.baseFret - b.baseFret)[0];
}

/** A playable shape for this chord, or null when we would be guessing. */
export function fingering(label: string): Shape | null {
  const parts = split(label);
  if (!parts) return null;

  const canonical = parts.root + parts.quality;
  if (OPEN[canonical]) return { frets: OPEN[canonical], baseFret: 1 };

  // Enharmonic spelling: Bb and A# are the same grip.
  const pitch = PITCH[parts.root];
  const alternate = Object.keys(PITCH).find((n) => PITCH[n] === pitch && n !== parts.root);
  if (alternate && OPEN[alternate + parts.quality]) {
    return { frets: OPEN[alternate + parts.quality], baseFret: 1 };
  }

  return movable(pitch, parts.quality);
}
